import { ALLERGENS } from "@foodlista/shared/allergens/constants"
import { z } from "zod"

/**
 * One item to enrich. Mirrors the sync `ProductMetadataInput` used by the
 * old inline detection: `legendAllergens` carries the menu-legend-derived
 * allergens so the run can preserve the "legend wins" precedence and only
 * fall back to the LLM for items with none.
 */
const detectMetadataItemSchema = z.object({
	id: z.string(),
	name: z.string(),
	category: z.string().optional(),
	description: z.string().optional(),
	legendAllergens: z.array(z.enum(ALLERGENS)),
})

export const menuDetectMetadataInputSchema = z.object({
	flowId: z.string().uuid(),
	/**
	 * Unread by the run — persistence is keyed by product id. It rides in the
	 * payload so the business app can attribute a live run to the menu whose
	 * card it should block.
	 */
	menuId: z.string().uuid(),
	/** Unread by the run; lets the BOC link the finished run to the menu page. */
	menuSlug: z.string().min(1).max(200).optional(),
	products: z.array(detectMetadataItemSchema).min(1),
})

/**
 * The run persists allergens + attributes server-side (keyed by the ids in
 * `products`), so the output only reports whether detection succeeded.
 * `false` means the products keep their empty metadata.
 */
export const menuDetectMetadataOutputSchema = z.object({
	success: z.boolean(),
})

/** Semantic phases persisted by the Trigger run for the BOC. */
export type MenuDetectMetadataProgressPhase =
	| { kind: "analyzing" }
	| { kind: "detected"; count: number }
	| { kind: "fallback" }
	| { kind: "done" }

export type MenuDetectMetadataInput = z.infer<
	typeof menuDetectMetadataInputSchema
>
export type MenuDetectMetadataOutput = z.infer<
	typeof menuDetectMetadataOutputSchema
>
